/**
 * A user's edits to the UPPER tier's bays: a width, a front, or no cabinet.
 *
 * The upper tier plans its boxes first (`upperTier.js` — mirrored along the
 * run, its own plan at a corner); this folds the run's `tier: 'upper'`
 * overrides into that plan. See
 * claude-docs/2026-09-19-upper-cabinet-tier-design.md §3.3–§3.4.
 *
 * "Match the lowers" is SEEDING, not a second algorithm. A stretch of wall
 * holding a fixed-width upper is re-packed through the SAME packer the lowers
 * use (`planSubSpanSequence`): every planned box the edit does not cover goes
 * in as a fixed piece of its own width — a seed — and the user's box replaces
 * the seeds it overlaps. What the edit frees is auto space, solved by the
 * run's family. So an edit disturbs only the seams it covers, clearing it puts
 * them back, and a stretch nobody edited never reaches the packer at all —
 * which is what keeps every existing run's uppers exactly where they were.
 *
 * Pure.
 */

const { UPPER_FRONT_TYPES: UPPER_FRONT_CATALOG } = require('./catalog');
const { planSubSpanSequence, SPAN_EPS } = require('./spanPacking');
const { overridesForSegment } = require('./bayOverrides');
const { INCHES_TO_METERS: INCHES } = require('../kitchen-assembly/constants');
const { seatParam } = require('./overrideHomes');
const { RACKS } = require('./racks');

/**
 * May an edit touch this box? A corner stretch's scribe is the corner plan's —
 * the strip that lets a cabinet die into the corner, not a bay. It BOUNDS the
 * editable stretches instead.
 */
const isEditable = (box) => !box.corner;

/**
 * The maximal runs of contiguous editable boxes on a segment — the stretches
 * of wall an upper edit can re-pack. Each is `{ lo, hi, indices }`, `indices`
 * into `boxes`.
 */
function editableStretches(boxes) {
  const order = boxes
    .map((box, index) => ({ box, index }))
    .filter(({ box }) => isEditable(box))
    .sort((a, b) => a.box.start - b.box.start);
  const stretches = [];
  for (const { box, index } of order) {
    const last = stretches[stretches.length - 1];
    if (last && Math.abs(box.start - last.hi) <= SPAN_EPS) {
      last.hi = box.end;
      last.indices.push(index);
    } else {
      stretches.push({ lo: box.start, hi: box.end, indices: [index] });
    }
  }
  return stretches;
}

/**
 * Where a fixed-width override nominally stands: its width centred on its
 * anchor, slid back inside the stretch when it overhangs an end. Only used to
 * decide which seeds it REPLACES — the packer decides where it lands.
 */
function nominalSpan(override, stretch) {
  const width = Math.min(override.width, stretch.hi - stretch.lo);
  const start = Math.min(
    Math.max(override.anchorParam - width / 2, stretch.lo),
    stretch.hi - width
  );
  return { start, end: start + width };
}

const overlap = (a, b) => Math.min(a.end, b.end) - Math.max(a.start, b.start);

/**
 * A width the user placed by dragging an EDGE (`positioned`, written by the
 * edge handle — `useRunDrag`'s `bay-width`): the edge they did not grab stays
 * where it was. The lower tier has honoured that since 2026-10-06 by handing
 * such a bay to the packer as a HARD piece (`bayOverrides.overridesForZone`);
 * the upper tier sequenced it like any width, so its low edge became the
 * running total of the boxes before it and the edge the user did NOT grab was
 * the one that moved (user report 2026-10-26).
 *
 * Same rule as below: held only while its body lies inside the stretch it was
 * placed in. One hanging past the stretch keeps its width and packs as
 * before.
 */
const isPositioned = (o) => o.positioned === true && o.width > 0;
const placedSpan = (o) => ({
  start: o.anchorParam - o.width / 2,
  end: o.anchorParam + o.width / 2,
});
const holdsPlaceIn = (o, stretch) => {
  const { start, end } = placedSpan(o);
  return (
    isPositioned(o) &&
    start >= stretch.lo - SPAN_EPS &&
    end <= stretch.hi + SPAN_EPS
  );
};

/** One packed piece back as a planned box. */
function boxFromPiece(piece) {
  // A placed upper, back from the packer as the hard piece it went in as.
  if (piece.kind === 'hard' && piece.bay?.cabinetOverride) {
    const override = piece.bay.cabinetOverride;
    return {
      start: piece.start,
      end: piece.end,
      ...boxFieldsOf(override),
      fixedWidth: true,
      override,
    };
  }
  const seed = piece.override?.seedOf;
  if (seed) return { ...seed, start: piece.start, end: piece.end };
  const override = piece.override || null;
  if (piece.kind === 'filler') {
    return { start: piece.start, end: piece.end, filler: true };
  }
  if (!override) return { start: piece.start, end: piece.end, filler: false };
  return {
    start: piece.start,
    end: piece.end,
    ...boxFieldsOf(override),
    fixedWidth: true,
    override,
  };
}

/**
 * Re-pack one stretch around its fixed-width overrides. Returns the new boxes,
 * or `{ overflow: true }` when the fixed widths cannot fit the stretch — the
 * caller leaves the stretch as planned and says so, rather than pack a box
 * past the wall it hangs on.
 */
function repackStretch(stretch, boxes, widthOverrides, fillOpts) {
  const placed = widthOverrides.filter((o) => holdsPlaceIn(o, stretch));
  const sequenced = widthOverrides.filter((o) => !placed.includes(o));
  const taken = [
    ...placed.map(placedSpan),
    ...sequenced.map((o) => nominalSpan(o, stretch)),
  ];
  const seeds = stretch.indices
    .map((i) => boxes[i])
    .filter((box) => taken.every((t) => overlap(box, t) <= SPAN_EPS));
  const fixed = [
    ...seeds.map((box) => ({
      anchorParam: (box.start + box.end) / 2,
      width: box.end - box.start,
      // The packer echoes `override` back opaquely; a seed rides on it so its
      // flags (a mirrored filler stays a filler) survive the round trip.
      override: { seedOf: box },
    })),
    ...sequenced.map((o) => ({
      anchorParam: o.anchorParam,
      width: o.width,
      override: o,
    })),
  ];
  const hard = placed.map((o) => ({
    center: o.anchorParam,
    width: o.width,
    bay: { kind: 'upper-cabinet', bayId: o.id, cabinetOverride: o },
  }));
  const packed = planSubSpanSequence(
    stretch.lo,
    stretch.hi,
    fixed,
    hard,
    fillOpts
  );
  if (packed.overflow) return { overflow: true };
  return {
    boxes: packed.pieces.map(boxFromPiece),
    // Two placed uppers on top of each other: the packer leaves both where
    // they stand and reports it, as it does for two scanned appliances.
    overlaps: (packed.unsatisfied || []).filter((u) => u.reason === 'overlap'),
  };
}

/**
 * Two uppers the user placed overlap. A FIT fault like `fitWarning`, so an
 * edit that makes one is refused (`runEditVerdict.introducedFaults`) — an
 * edge dragged into a pinned neighbour used to save the two through each
 * other in silence (user report 2026-10-26) — and a run saved that way says
 * so instead of drawing it.
 */
function overlapWarning(u) {
  return {
    severity: 'warning',
    type: 'fit',
    bayId: u.bay?.bayId ?? null,
    message: `Two upper cabinets you placed overlap by ${Math.round(
      u.delta / INCHES
    )}" — move one, or unpin it so the run places it.`,
  };
}

/** The user's fixed widths do not fit the wall they stand on. */
function fitWarning(widthOverrides, span) {
  const total = widthOverrides.reduce((sum, o) => sum + o.width, 0);
  return {
    severity: 'warning',
    type: 'fit',
    message: `An upper cabinet set to ${Math.round(
      total / INCHES
    )}" does not fit the ${Math.round(
      (span.hi - span.lo) / INCHES
    )}" of wall it hangs on — it was left as the run sized it.`,
  };
}

const anchoredIn = (o, lo, hi) =>
  o.anchorParam >= lo - SPAN_EPS && o.anchorParam <= hi + SPAN_EPS;

/**
 * The fronts an upper bay may wear besides plain doors — derived from the
 * catalog (`catalog.js`) rather than restated, so the picker cannot offer a
 * front this pass does not recognise, or withhold one it does. `standard` is
 * the ABSENCE of an override here, which is why it is filtered out.
 */
const UPPER_FRONT_TYPES = new Set(
  UPPER_FRONT_CATALOG.map((f) => f.id).filter((id) => id !== 'standard')
);

/** What an override says about the BOX it governs, width aside. */
function boxFieldsOf(override) {
  return {
    filler: override.type === 'filler',
    none: override.type === 'none',
    openShelf: override.type === 'open-shelf',
    floatingShelves: override.type === 'floating-shelves',
    endShelf: override.type === 'end-shelf',
    angledEnd: override.type === 'angled-end',
    microwaveShelf: override.type === 'microwave-shelf',
    wallDrawers: override.type === 'wall-drawers',
    // no cabinet, the valance board's box (`valanceBays.js`)
    valance: override.type === 'valance',
    // an open rack (`racks.js`): which one
    ...(RACKS[override.type] ? { rack: override.type } : {}),
    // A bridge cabinet: shorter than the run's uppers, top on the same line.
    ...(override.height > 0 ? { height: override.height } : {}),
  };
}

/** Does a width-less record change the BOX (its front or height)? */
const changesBox = (o) => UPPER_FRONT_TYPES.has(o.type) || o.height > 0;

/**
 * A segment's upper-tier overrides, split by what they do: a WIDTH re-packs
 * the stretch it stands in; anything else — a front, a height, an appearance
 * — is SEATED on one box and disturbs nothing beside it.
 */
function upperOverridesFor(run, segIndex, boxes = []) {
  const overrides = overridesForSegment(run, segIndex, 'upper').map((o) =>
    homedToSink(o, boxes)
  );
  return {
    widthOverrides: overrides.filter((o) => o.width > 0),
    seatedOverrides: overrides.filter((o) => !(o.width > 0)),
  };
}

/**
 * A valance bay's record over the sink base it was picked over
 * (`valanceBays.js`): seated on that base's box this solve wherever the sink
 * now stands, and a width it holds placed there, centred (`positioned`), so
 * the cabinets either side refit round it. A record whose sink has no box
 * this solve keeps its point. Marked `homedToSink` so its seat is the sink's,
 * not the box it was worn by last solve.
 */
function homedToSink(o, boxes) {
  if (o.type !== 'valance' || !o.overSinkBayId) return o;
  const box = boxes.find((b) => b.overSinkBayId === o.overSinkBayId);
  if (!box) return o;
  return {
    ...o,
    anchorParam: (box.start + box.end) / 2,
    ...(o.width > 0 ? { positioned: true } : {}),
    homedToSink: true,
  };
}

/** May a width-less record be seated on this box? */
const seatable = (b) => isEditable(b) && !b.fixedWidth;

/**
 * Seat a width-less override on its box: the one that wore it last solve
 * (`prevSpan`, `overrideHomes.seatParam`), else the one its stored point
 * stands in. A front or a height changes the box and is carried on it as
 * `override`. A record that changes nothing about the box — an appearance
 * alone — rides `seatedRecord` instead: `override` is what every planning
 * rule reads as "the user edited this box" (a blind corner's re-cut leaves
 * such a box alone), and a paint colour must not change what is built. Either
 * way the appearance pass reads the record THIS pass chose (`fillRun`), so the
 * two cannot disagree about which box it is. The first record seated on a box
 * keeps it.
 */
function applyTypeOverride(boxes, override, prevSpan) {
  const at = seatParam(boxes.filter(seatable), override, prevSpan);
  const idx = boxes.findIndex(
    (b) => seatable(b) && at >= b.start - SPAN_EPS && at <= b.end + SPAN_EPS
  );
  if (idx < 0) return boxes;
  if (!changesBox(override)) {
    return boxes.map((b, i) =>
      i !== idx || b.override || b.seatedRecord
        ? b
        : { ...b, seatedRecord: override }
    );
  }
  const fields = boxFieldsOf(override);
  return boxes.map((b, i) =>
    i !== idx
      ? b
      : {
          ...b,
          ...fields,
          // A height alone leaves a mirrored filler the filler it was.
          filler: fields.filler || (!override.type && b.filler),
          override,
        }
  );
}

/**
 * Fold a segment's upper-tier overrides into its planned boxes.
 *
 *  - `width` — a fixed-width upper. Locked in width, not in position: it
 *    slides so the stretch packs flush, exactly as a lower fixed bay does —
 *    unless it was placed by an edge drag (`positioned`), which holds its
 *    place while the stretch reaches around it, as a lower one does.
 *  - `type` — `'filler'` (a blank panel), `'open-shelf'` (the box with no
 *    doors) or `'none'` (no cabinet: the wall stays bare). With a width,
 *    `'none'` is a gap of exactly that width; without, it clears the box its
 *    anchor stands in.
 *  - `height` — a BRIDGE cabinet: shorter than the run's uppers, its top on
 *    the same line and its bottom raised (`upperTier.bottomOver`). Never
 *    taller than the run's own.
 *
 * Boxes nobody edited come back untouched and in their original order.
 *
 * @param {Object[]} boxes planned boxes `{ start, end, filler, corner? }`
 * @param {Map<string, {start, end}>} prevSpans the boxes that wore each
 *   record last solve (`overrideHomes.previousSpansOf`) — where a width-less
 *   record is seated
 * @returns {{ boxes: Object[], warnings: Object[] }} `boxes` still carries the
 *   `none` ones — the caller drops them at emit, after every rule that reads
 *   a neighbour has seen the gap as occupied.
 */
function applyUpperOverrides(
  run,
  segIndex,
  boxes,
  fillOpts,
  prevSpans = new Map()
) {
  const { widthOverrides, seatedOverrides } = upperOverridesFor(
    run,
    segIndex,
    boxes
  );
  const warnings = [];
  if (!widthOverrides.length && !seatedOverrides.length) {
    return { boxes, warnings };
  }

  // A re-packed stretch's boxes land where its FIRST box stood in the plan,
  // and its other boxes leave; everything else keeps its place. Built in one
  // pass over the ORIGINAL indices — a stretch's boxes need not be neighbours
  // in `boxes` (a corner's 12" cabinet is planned after the mirrored ones it
  // stands beside), so splicing as we go would shift what later indices mean.
  const replacement = new Map();
  const replaced = new Set();
  for (const stretch of editableStretches(boxes)) {
    const here = widthOverrides.filter((o) =>
      anchoredIn(o, stretch.lo, stretch.hi)
    );
    if (!here.length) continue;
    const packed = repackStretch(stretch, boxes, here, fillOpts);
    if (packed.overflow) {
      warnings.push(fitWarning(here, stretch));
      continue;
    }
    warnings.push(...packed.overlaps.map(overlapWarning));
    replacement.set(Math.min(...stretch.indices), packed.boxes);
    for (const i of stretch.indices) replaced.add(i);
  }
  let out = boxes.flatMap((box, i) => {
    if (replacement.has(i)) return replacement.get(i);
    return replaced.has(i) ? [] : [box];
  });
  for (const o of seatedOverrides)
    out = applyTypeOverride(
      out,
      o,
      o.homedToSink ? undefined : prevSpans.get(o.id)
    );
  return { boxes: out, warnings };
}

/**
 * The upper tier packed ON ITS OWN — `upperSpec.align: 'independent'`.
 *
 * Nothing is mirrored and nothing is seeded: each clear span of upper wall is
 * packed through the same packer as EQUAL modules (`opts.equalModules`, user
 * ruling 2026-09-19), a user's fixed-width uppers holding their width among
 * them and the rest of the span re-equalising around them. A width the span
 * cannot hold is refused with a warning and the span packs without it.
 *
 * @param {{ lo, hi, loBound, hiBound }[]} spans the clear spans, each naming
 *   what stands at its two ends (`fillerEnd.js`'s vocabulary) — that decides
 *   which end a scribe dies into and which way an uneven pair faces
 * @returns {{ boxes: Object[], warnings: Object[] }} as `applyUpperOverrides`
 */
function packIndependentSpans(
  run,
  segIndex,
  spans,
  fillOpts,
  prevSpans = new Map()
) {
  const { widthOverrides, seatedOverrides } = upperOverridesFor(run, segIndex);
  const warnings = [];
  let boxes = [];
  for (const span of spans) {
    const opts = {
      ...fillOpts,
      equalModules: true,
      loBound: span.loBound,
      hiBound: span.hiBound,
    };
    const pack = (overrides) =>
      planSubSpanSequence(
        span.lo,
        span.hi,
        overrides.map((o) => ({
          anchorParam: o.anchorParam,
          width: o.width,
          override: o,
        })),
        [],
        opts
      );
    const here = widthOverrides.filter((o) => anchoredIn(o, span.lo, span.hi));
    let packed = pack(here);
    if (packed.overflow) {
      warnings.push(fitWarning(here, span));
      packed = pack([]);
    }
    boxes.push(...packed.pieces.map(boxFromPiece));
  }
  for (const o of seatedOverrides)
    boxes = applyTypeOverride(boxes, o, prevSpans.get(o.id));
  return { boxes, warnings };
}

module.exports = {
  UPPER_FRONT_TYPES,
  applyUpperOverrides,
  packIndependentSpans,
  editableStretches,
};
